import React, { useState, useEffect } from 'react';
import { signInWithEmail, getSession } from '../../lib/supabase';
import { isValidEmail, isDisposableEmail, getFreeLimit } from '../../lib/freemium';

export default function EmailGateModal({ lang, onClose }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const ar = lang === 'ar';
  const L = (fr, a) => ar ? a : fr;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email) { setError(L('Entrez votre email', 'أدخل بريدك الإلكتروني')); return; }
    if (!isValidEmail(email)) { setError(L('Email invalide', 'بريد إلكتروني غير صالح')); return; }
    if (isDisposableEmail(email)) { setError(L('Les emails temporaires ne sont pas acceptés', 'البريد الإلكتروني المؤقت غير مقبول')); return; }

    setSending(true);
    const { success, error: err } = await signInWithEmail(email);
    setSending(false);

    if (success) {
      setStep(2);
    } else {
      setError(err || L('Erreur d\'envoi. Réessayez.', 'خطأ في الإرسال. حاول مرة أخرى.'));
    }
  };

  const close = () => { if (onClose) onClose(); };

  // Poll for session when waiting for email click
  useEffect(() => {
    if (step !== 2) return;
    const interval = setInterval(async () => {
      const session = await getSession();
      if (session) { clearInterval(interval); window.location.reload(); }
    }, 3000);
    return () => clearInterval(interval);
  }, [step]);

  return (
    <div onClick={close} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }} dir={ar ? 'rtl' : 'ltr'}>
      <div onClick={e => e.stopPropagation()} style={{ background:'#141927', borderRadius:20, padding:'2rem', maxWidth:420, width:'90%', boxShadow:'0 20px 60px rgba(0,0,0,0.4)', textAlign:'center', position:'relative', border:'1px solid rgba(255,255,255,0.06)' }}>
        <button onClick={close} style={{ position:'absolute', top:12, right:14, background:'none', border:'none', fontSize:'1.2rem', cursor:'pointer', color:'#94A3B8', zIndex:10 }}>✕</button>

        {step === 1 && (<>
          <div style={{ fontSize:'2.5rem', marginBottom:'0.5rem' }}>🔓</div>
          <h2 style={{ fontSize:'1.2rem', color:'#F8FAFC', marginBottom:'0.3rem' }}>{L('Continuez gratuitement', 'تابع مجاناً')}</h2>
          <p style={{ fontSize:'0.82rem', color:'#94A3B8', marginBottom:'1.2rem', lineHeight:1.6 }}>
            {L(
              `Vous avez utilisé vos ${getFreeLimit()} analyses gratuites ce mois-ci. Entrez votre email pour continuer — c'est totalement gratuit.`,
              `لقد استخدمت تحليلاتك ${getFreeLimit()} المجانية هذا الشهر. أدخل بريدك الإلكتروني للمتابعة — مجاني تماماً.`
            )}
          </p>
          <form onSubmit={handleSubmit}>
            <input type="email" value={email} onChange={e => { setEmail(e.target.value); setError(''); }}
              placeholder={L('votre@email.com', 'بريدك@email.com')}
              style={{ width:'100%', padding:'0.7rem', border:'1px solid rgba(255,255,255,0.1)', borderRadius:10, fontSize:'0.9rem', marginBottom:'0.5rem', direction:'ltr', textAlign:'left' }} />
            {error && <div style={{ color:'#dc2626', fontSize:'0.78rem', marginBottom:'0.5rem' }}>{error}</div>}
            <button type="submit" disabled={sending}
              style={{ width:'100%', padding:'0.75rem', background:'#7C3AED', color:'white', border:'none', borderRadius:10, fontSize:'0.9rem', fontWeight:600, cursor:'pointer', opacity: sending ? 0.6 : 1 }}>
              {sending ? '...' : L('Recevoir le lien de connexion', 'استلام رابط الدخول')}
            </button>
          </form>
          <p style={{ fontSize:'0.68rem', color:'#64748b', marginTop:'0.8rem' }}>
            {L('Un lien magique sera envoyé à votre email. Pas de mot de passe.', 'سيتم إرسال رابط سحري إلى بريدك. بدون كلمة مرور.')}
          </p>
        </>)}

        {step === 2 && (<>
          <div style={{ fontSize:'2.5rem', marginBottom:'0.5rem' }}>📧</div>
          <h2 style={{ fontSize:'1.2rem', color:'#F8FAFC', marginBottom:'0.5rem' }}>{L('Vérifiez votre boîte email', 'تحقق من بريدك الإلكتروني')}</h2>
          <p style={{ fontSize:'0.88rem', color:'#94A3B8', lineHeight:1.7 }}>
            {L(
              `Un lien magique a été envoyé à :`,
              `تم إرسال رابط سحري إلى:`
            )}
          </p>
          <p style={{ fontSize:'1rem', color:'#818cf8', fontWeight:700, margin:'0.5rem 0' }}>{email}</p>
          <div style={{ background:'rgba(124,58,237,0.08)', border:'1px solid rgba(124,58,237,0.15)', borderRadius:12, padding:'0.8rem', margin:'0.8rem 0', textAlign: ar ? 'right' : 'left' }}>
            <p style={{ fontSize:'0.82rem', color:'#F8FAFC', marginBottom:'0.4rem', fontWeight:600 }}>{L('Comment ça marche :', 'كيف يعمل:')}</p>
            <p style={{ fontSize:'0.78rem', color:'#94A3B8', lineHeight:1.7 }}>
              {L(
                '1. Ouvrez votre boîte email\n2. Cliquez sur le lien dans l\'email de TidjarAI\n3. Vous serez automatiquement connecté\n\n💡 Le lien fonctionne sur tous vos appareils. Si vous revenez sur cette page, vous serez déjà connecté.',
                '1. افتح بريدك الإلكتروني\n2. انقر على الرابط في بريد TidjarAI\n3. ستكون متصلاً تلقائياً\n\n💡 الرابط يعمل على جميع أجهزتك. إذا عدت إلى هذه الصفحة، ستكون متصلاً بالفعل.'
              ).split('\n').map((line, i) => <span key={i}>{line}<br/></span>)}
            </p>
          </div>
          <p style={{ fontSize:'0.72rem', color:'#64748b', marginTop:'0.5rem' }}>
            {L('⚠️ Vérifiez aussi vos spams. Le lien expire dans 24h.', '⚠️ تحقق أيضاً من مجلد البريد المزعج. الرابط صالح لمدة 24 ساعة.')}
          </p>
          <button onClick={close} style={{ marginTop:'1rem', padding:'0.6rem 1.5rem', background:'transparent', color:'#94A3B8', border:'1px solid rgba(255,255,255,0.1)', borderRadius:10, cursor:'pointer', fontSize:'0.82rem' }}>
            {L('Fermer', 'إغلاق')}
          </button>
        </>)}
      </div>
    </div>
  );
}
